import '~/modules/label-perf/styles/label-perf-override.css';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlideOut } from '~/chords/SlideOut';
import { Icon, Separator, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import { cn } from '~/lib/cn';
import type { UserWithActions } from '~/modules/auth';
import { getTrackOverwrite } from '~/modules/label-perf/api/overwrites';
import { escapeAudioPresentation } from '~/modules/label-perf/api/related-tracks';
import type { TrackLock } from '~/modules/label-perf/api/track-lock';
import type { DashboardItem } from '~/modules/label-perf/types';

import { Comment } from '../../Dashboard/components/Comment';
import { TrackOverwrite } from '../../Dashboard/components/TrackOverwrite';
import { autoPluralize, countryRenderer } from '../../Dashboard/renderers';
import { FieldBlock, FieldValue } from './Fields';
import { QuickList } from './QuickList';
import { RelatedTracks } from './RelatedTracks';
import { TableTd, TableTh } from './Table';

const customScrollClassName = 'custom-scroll';

const mothershipUrl = 'https://mothership.wmg.cloud';

interface GeneralInfoProps {
  isrc: string;
  data: DashboardItem;
  trackLock: TrackLock | null;
  handleTrackLock: () => Promise<void>;
  trackIgnore: TrackLock | null;
  handleTrackIgnore: () => Promise<void>;
  currentUser: Partial<UserWithActions>;
}

function GeneralInfo(props: GeneralInfoProps): React.JSX.Element {
  const { t } = useTranslation('label-perf');
  const [processing, setProcessing] = useState(false);
  const [typeToggle, setTypeToggle] = useState<'same' | 'all'>('same');
  const [overwriteDialog, setOverwriteDialog] = useState(false);
  const [overwrites, setOverwrites] = useState<{
    trackTitle?: string;
    artistName?: string;
  }>({});
  const { data, trackLock, trackIgnore, currentUser } = props;

  useEffect(() => {
    getTrackOverwrite(props.isrc)
      .then((_data) => {
        setOverwrites({
          trackTitle: _data?.track_title,
          artistName: _data?.artist_name,
        });

        return null;
      })
      .catch((error) => {
        console.error('Error fetching track overwrite data:', error);
        setOverwrites({}); // Reset overwrites on error
      });
  }, [props.isrc, overwriteDialog]);

  const handleTrackLock = (): void => {
    setProcessing(true);
    props
      .handleTrackLock()
      .then(() => {
        setProcessing(false);

        return null;
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleTrackIgnore = (): void => {
    setProcessing(true);
    props
      .handleTrackIgnore()
      .then(() => {
        setProcessing(false);

        return null;
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <div>
      {/* TRACK INFO */}
      <div className="my-xl grid grid-cols-[1fr_18em_1fr_22em] gap-xs">
        {/* B1 */}
        <div className="border-r pr-xl border-emphasis-subtle">
          <div>
            <Tooltip
              delayDuration={0}
              side="bottom"
              size="md"
              content={
                <span>
                  {data?.trackTitle ?? ''}{' '}
                  {overwrites.trackTitle ? ` - ${overwrites.trackTitle}` : ''}
                </span>
              }
            >
              <p className="max-w-[10em] truncate text-display-md font-bold">
                {data?.trackTitle ?? ''}
                {overwrites.trackTitle ? (
                  <span>
                    <Icon
                      type="marker"
                      variant="solid"
                      size="xs"
                      className="ml-sm !text-body-sm text-subtle"
                    />
                  </span>
                ) : null}
              </p>
            </Tooltip>
          </div>

          <p className="mb-md text-body-xs font-bold">
            {data.artist}
            {overwrites.artistName ? (
              <span title={overwrites.artistName}>
                <Icon
                  type="marker"
                  variant="solid"
                  size="sm"
                  className="ml-sm text-subtle"
                />
              </span>
            ) : null}
          </p>

          <FieldBlock className="mb-md" label={t('body.majorLabel')}>
            <Tooltip
              delayDuration={0}
              side="bottom"
              size="md"
              content={<span>{data?.majorLabel[0] ?? ''}</span>}
            >
              <p className="rounded-xs px-sm py-xs text-body-xs font-bold bg-emphasis-subtle">
                {data.majorLabel[0] || '--'}
              </p>
            </Tooltip>
          </FieldBlock>

          <FieldBlock className="mb-0" label="ISRC">
            <a
              className="flex cursor-pointer items-center font-light text-default hover:underline"
              href={`${mothershipUrl}/repertoire/recording/${data?.isrc}`}
              target="_blank"
              rel="noreferrer"
            >
              {data?.isrc}{' '}
              <Icon
                type="external-link"
                variant="solid"
                size="sm"
                className="ml-sm"
              />
            </a>
          </FieldBlock>
        </div>

        {/* B2 */}
        <div className="border-r px-xl border-emphasis-subtle">
          <FieldValue label="Asset Type" value={data?.recType ?? '--'} />
          <FieldValue label="Asset Sub Type" value={data?.recSubType ?? '--'} />
          <FieldValue
            label="Track Version"
            value={data?.recordingVersion ?? '--'}
          />
          <FieldValue
            className="mb-0"
            label="Audio Presentation"
            value={escapeAudioPresentation(data?.audioPresentation) ?? '--'}
          />
        </div>

        {/* B3 */}
        <div className="px-xl">
          <FieldValue
            label="PA Flag"
            value={data?.paFlag === 'Y' ? 'Yes' : 'No'}
          />

          <FieldValue label="Media Code" value={data?.mediaCode ?? '--'} />
          <FieldValue className="hidden" label="" value="" />

          {!processing && trackLock ? (
            <div className="flex items-center align-middle text-body-xs text-white">
              <p className="whitespace-nowrap font-light">
                {t('body.inReviewBy')}{' '}
              </p>
              <span className="mx-xs w-[10em] truncate font-bold">
                {trackLock.created_by.email === currentUser.email
                  ? 'yourself'
                  : trackLock.created_by.name}
              </span>{' '}
              {t('on')}
              <span className="ml-xs font-bold">
                {new Date(trackLock.created_at).toLocaleDateString()}
              </span>
            </div>
          ) : null}

          {!processing && trackIgnore ? (
            <div className="flex items-center align-middle text-body-xs text-white">
              <p className="whitespace-nowrap font-light">
                {t('body.disabledBy')}{' '}
              </p>
              <span className="mx-xs w-[10em] truncate font-bold">
                {trackIgnore.created_by.email === currentUser.email
                  ? 'yourself'
                  : trackIgnore.created_by.name}
              </span>{' '}
              {t('on')}
              <span className="ml-xs font-bold">
                {new Date(trackIgnore.created_at).toLocaleDateString()}
              </span>
            </div>
          ) : null}
        </div>

        {/* ACTIONS */}
        <div className="flex h-[2.2em] justify-end gap-md">
          {processing ? (
            <div
              data-testid="processing-spinner"
              className="mt-xs flex items-center px-sm align-middle text-body-sm text-white"
            >
              <p>
                <Icon
                  type="spinner"
                  variant="solid"
                  size="sm"
                  className="fa-spin mr-sm text-white"
                />
                {t('body.processing')}
              </p>
            </div>
          ) : null}

          {!processing && (
            <Button
              className="text-body-sm"
              variant="primary"
              onClick={() => setOverwriteDialog(true)}
            >
              <Icon type="marker" variant="solid" size="sm" className="mr-sm" />
              {t('actions.edit')}
            </Button>
          )}

          {!processing && !trackLock && !trackIgnore && (
            <Button
              onClick={handleTrackLock}
              variant="secondary"
              className="text-body-sm"
            >
              <Icon type="eye" variant="solid" size="sm" className="mr-sm" />
              {t('body.inReview')}
            </Button>
          )}

          {!processing &&
          trackLock &&
          trackLock.created_by.email === currentUser.email ? (
            <Button
              className="text-body-sm"
              variant="secondary"
              onClick={handleTrackLock}
            >
              <Icon
                type="eye-slash"
                variant="solid"
                size="sm"
                className="mr-sm"
              />
              {t('actions.cancelReview')}
            </Button>
          ) : null}

          {!processing && !trackLock && !trackIgnore && (
            <Button
              className="text-body-sm"
              variant="secondary"
              onClick={handleTrackIgnore}
            >
              <Icon type="ban" variant="solid" size="sm" className="mr-sm" />
              {t('actions.disable')}
            </Button>
          )}

          {!processing && trackIgnore ? (
            <Button
              className="text-body-sm"
              variant="secondary"
              onClick={handleTrackIgnore}
            >
              <Icon
                type="circle-check"
                variant="solid"
                size="sm"
                className="mr-sm"
              />
              {t('actions.enable')}
            </Button>
          ) : null}
        </div>
      </div>

      <Separator />

      {/* RELEASES */}
      <div className="mt-xl">
        <h2 className="text-body-md font-bold text-white">
          {t('body.releases')}
        </h2>

        <div
          className={cn(
            'mt-md overflow-hidden overflow-x-auto rounded-sm border border-emphasis-subtle',
            customScrollClassName,
          )}
        >
          <table className="w-full">
            <thead>
              <tr className="bg-level-02">
                <TableTh>{t('table.GPID')}</TableTh>
                <TableTh>{t('table.GRID')}</TableTh>
                <TableTh>{t('table.albumTitle')}</TableTh>
                <TableTh>{t('table.albumSubTitle')}</TableTh>
                <TableTh>{t('table.releaseType')}</TableTh>
                <TableTh>{t('table.majorLabel')}</TableTh>
                <TableTh>{t('table.presentationLabel')}</TableTh>
                <TableTh>{t('table.audioPres')}</TableTh>
                <TableTh>{t('table.PA')}</TableTh>
                <TableTh>
                  <span className="flex items-center self-end">
                    <Icon
                      type="check-circle"
                      variant="solid"
                      size="sm"
                      className="mr-sm"
                    />
                    {t('table.dbom')}
                  </span>
                </TableTh>
                <TableTh>{t('Comment')}</TableTh>
              </tr>
            </thead>
            <tbody>
              {data?.gpid.map((gpid, i) => {
                return (
                  <tr
                    className={cn(
                      'h-[3.2em] border border-emphasis-subtle',
                      i % 2 === 0 ? 'bg-level-03' : 'bg-level-02',
                    )}
                    key={gpid}
                  >
                    <TableTd>
                      <a
                        className="flex cursor-pointer items-center underline"
                        href={`${mothershipUrl}/overview;product=${gpid}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {gpid}
                        <Icon
                          type="external-link"
                          variant="solid"
                          size="sm"
                          className="ml-sm"
                        />
                      </a>
                    </TableTd>
                    <TableTd>{data?.grid[i] ?? '--'}</TableTd>
                    <TableTd>{data?.albumTitle[i] ?? '--'}</TableTd>
                    <TableTd>{data?.albumSubtitle[i] ?? '--'}</TableTd>
                    <TableTd>{data?.releaseType[i] ?? '--'}</TableTd>
                    <TableTd>{data?.majorLabel[i] ?? '--'}</TableTd>
                    <TableTd>{data?.presentationLabel[i] ?? '--'}</TableTd>
                    <TableTd>
                      {data?.productAudioPresentations[i] ?? '--'}
                    </TableTd>
                    <TableTd>{data?.productPaFlags[i] ?? 'N'}</TableTd>
                    <TableTd>{data?.completeDboms[i] ?? '--'}</TableTd>
                    <TableTd>
                      <Comment value={data?.internalComments[i]} />
                    </TableTd>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* RELATED TRACKS */}
      <div className="mt-lg">
        <div className="flex justify-between py-sm">
          <h2 className="mb-sm self-end text-body-md font-bold text-white">
            {t('body.relatedTracks')}
          </h2>
          <div className="mb-sm flex items-center text-white">
            <span className="ml-xs mr-md text-body-sm font-bold">
              {t('body.mediaCode')}:
            </span>
            <div className="flex gap-sm rounded-sm border-b p-xs bg-level-01 border-emphasis-subtle">
              <button
                className={cn(
                  'self-baseline rounded-sm px-md py-sm text-body-sm font-bold hover:text-tertiary-default',
                  typeToggle === 'same' && 'bg-level-05 text-tertiary-default',
                )}
                disabled={typeToggle === 'same'}
                onClick={() => setTypeToggle('same')}
              >
                {t('body.same')}
              </button>
              <button
                className={cn(
                  'self-baseline rounded-sm px-md py-sm text-body-sm font-bold hover:text-tertiary-default',
                  typeToggle === 'all' && 'bg-level-05 text-tertiary-default',
                )}
                disabled={typeToggle === 'all'}
                onClick={() => setTypeToggle('all')}
              >
                {t('body.all')}
              </button>
            </div>
          </div>
        </div>

        <div>
          <RelatedTracks
            isrc={props.isrc}
            mediaCode={typeToggle === 'same' ? data?.mediaCode : undefined}
          />
        </div>
      </div>

      {/* PARTNERS DELIVERY */}
      <div className="mt-lg">
        <h2 className="mb-md text-body-md font-bold text-white">
          {t('body.partnersDelivery')}
        </h2>
        <div className="rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
          <FieldValue
            label={t('body.deliveredTo')}
            className="mb-md text-body-sm"
            value={`${data?.partnerCount ?? 0} ${autoPluralize(data?.partnerCount, 'partner', 'partners')}`}
          />{' '}
          <QuickList className="w-1/2" values={data?.partnerList} />
        </div>
      </div>

      {/* TERRITORIES DELIVERY */}
      <div className="mt-lg">
        <h2 className="mb-md text-body-md font-bold text-white">
          {t('body.territoriesDelivery')}
        </h2>
        <div className="grid grid-cols-2 gap-lg rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
          <div>
            <FieldValue
              label={t('body.deliveredTo')}
              className="mb-md text-body-sm"
              value={`${data?.deliveryTerritoryCount ?? 0} ${autoPluralize(data?.deliveryTerritoryCount, 'territory', 'territories')}`}
            />
            <QuickList
              values={data?.deliveryTerritoryList.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
          <div>
            <FieldValue
              label={t('body.missedTerritories')}
              className="mb-md text-body-sm"
              value={`${data?.missedTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <QuickList
              values={data?.missedTerritoriesAlt.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
        </div>
      </div>

      {/* APPLE DELIVERY */}
      <div className="mt-lg">
        <h2 className="mb-md text-body-md font-bold text-white">
          {t('body.appleDelivery')}
        </h2>
        <div className="grid grid-cols-2 gap-lg rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
          <div>
            <FieldValue
              label={t('body.deliveredTo')}
              className="mb-md text-body-sm"
              value={`${data?.appleTerritoryCount ?? 0} ${autoPluralize(data?.appleTerritoryCount, 'territory', 'territories')}`}
            />

            <QuickList
              values={data?.appleTerritoryList.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
          <div>
            <FieldValue
              label={t('body.missedTerritories')}
              className="mb-md text-body-sm"
              value={`${data?.missedAppleTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedAppleTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <QuickList
              values={data?.missedAppleTerritoriesAlt.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
        </div>
      </div>

      {/* SPOTIFY DELIVERY */}
      <div className="mt-lg">
        <h2 className="mb-md text-body-md font-bold text-white">
          {t('body.spotifyDelivery')}
        </h2>
        <div className="grid grid-cols-2 gap-lg rounded-sm border px-lg py-xl bg-level-02 border-emphasis-subtle">
          <div>
            <FieldValue
              label={t('body.deliveredTo')}
              className="mb-md text-body-sm"
              value={`${data?.spotifyTerritoryCount ?? 0} ${autoPluralize(data?.spotifyTerritoryCount, 'territory', 'territories')}`}
            />
            <QuickList
              values={data?.spotifyTerritoryList.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
          <div>
            <FieldValue
              label={t('body.missedTerritories')}
              className="mb-md text-body-sm"
              value={`${data?.missedSpotifyTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedSpotifyTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <QuickList
              values={data?.missedSpotifyTerritoriesAlt.map((c) =>
                countryRenderer(c, 'detailed'),
              )}
            />
          </div>
        </div>
      </div>

      <SlideOut
        open={overwriteDialog}
        onOpenChange={() => setOverwriteDialog(false)}
        panelWidth="560px"
      >
        <TrackOverwrite
          closeHandler={() => setOverwriteDialog(false)}
          isrc={props.isrc}
        />
      </SlideOut>
    </div>
  );
}

GeneralInfo.displayName = 'GeneralInfo';

export { GeneralInfo };
